// Sound, made in the browser (Web Audio): nothing is downloaded. Each period has its own package of sounds (score.ts
// decides which an event gets; foley.ts renders them once, in a worker, as samples; sirens, aircraft and falling
// shells are played live). Everything is heard from where it happens on screen (panned, quieter and duller far
// away, and late: the flash comes first); atrocities get a distant bell, never a bang. Plays only after a click
// (browser rule).

import { RECIPES, renderSample, type SampleId } from "./foley";
import { ERA_BLADE, ERA_POWDER, eraOf, FX_BATTLE, FX_MEMORIAL, FX_NUKE } from "./logic";
import { LIVE, repeatTimes, samplesFor, scoreAmbient, scoreDistant, scoreEvent, type Cue } from "./score";

const MAX_VOICES = 48;
const LEAD = 0.6; // cues are built this long before they sound (an event's few seconds of sound, built as they come)

function noiseBuffer(ctx: BaseAudioContext, seconds: number, color: "white" | "brown" | "pink"): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let last = 0, b0 = 0, b1 = 0, b2 = 0;
  for (let i = 0; i < len; i++) {
    const w = Math.random() * 2 - 1;
    if (color === "white") d[i] = w;
    else if (color === "brown") {
      last = (last + 0.02 * w) / 1.02;
      d[i] = last * 3.5;
    } else {
      b0 = 0.99765 * b0 + w * 0.099046;
      b1 = 0.963 * b1 + w * 0.2965164;
      b2 = 0.57 * b2 + w * 1.0526913;
      d[i] = (b0 + b1 + b2 + w * 0.1848) * 0.2;
    }
  }
  return buf;
}

function impulse(ctx: BaseAudioContext, seconds: number, decay: number): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
  }
  return buf;
}

const idle = (fn: () => void) => {
  const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
  if (w.requestIdleCallback) w.requestIdleCallback(fn, { timeout: 500 });
  else window.setTimeout(fn, 16);
};

const clamp = (x: number, a: number, b: number) => Math.min(b, Math.max(a, x));

export interface Place { dist: number; pan: number } // see logic.hearing

interface Rendered { id: SampleId; v: number; sr: number; channels: Float32Array[] }

export class Engine {
  private ctx: BaseAudioContext | null = null;
  private live = true; // false for an offline context (rendering to a file)
  private master!: GainNode;
  private bus!: GainNode;
  private verb!: GainNode;
  private fight!: GainNode; // battle sounds, and their echo: turned down for a moment of quiet or a bell
  private fightVerb!: GainNode;
  private white!: AudioBuffer;
  private brown!: AudioBuffer;
  private pink!: AudioBuffer;
  private bed: { g: GainNode; lp: BiquadFilterNode; wind: GainNode; drone: GainNode; fire: GainNode | null } | null = null;
  private voices = 0;
  private enabled = false;
  private volume = 0.7;
  private duckUntil = 0;
  private lastBell = -10;
  private lastStinger = -10;
  private recent = new Map<string, number[]>();
  private hidden = false;
  // the period sounds: rendered variants, what has been asked for, and who renders them
  private bank = new Map<SampleId, AudioBuffer[]>();
  private asked = new Set<string>();
  private pending = new Set<string>();
  private prepared = new Set<number>();
  private worker: Worker | null | undefined;
  private watchdog = 0;
  private queue: { id: SampleId; v: number }[] = [];
  private pumping = false;
  private gaveUp = new Set<SampleId>(); // too slow to make without the worker: played without
  private jobs: { due: number; bell: boolean; run: () => void }[] = []; // cues waiting to be built
  private workerReady = false;
  private ticker = 0;
  private quiet = false;
  onBlocked: (blocked: boolean) => void = () => {};

  get ready() { return !!this.ctx && (!this.live || this.ctx.state === "running"); }

  /** For development: what is playing and what has been made. */
  get stats() {
    let buffers = 0, bytes = 0;
    for (const list of this.bank.values()) for (const b of list) { buffers++; bytes += b.length * b.numberOfChannels * 4; }
    return { state: this.ctx?.state ?? "none", voices: this.voices, sounds: this.bank.size, buffers, mb: +(bytes / 1e6).toFixed(1),
      pending: this.pending.size, worker: !!this.worker, quiet: this.quiet, fight: this.fight ? +this.fight.gain.value.toFixed(2) : 1 };
  }

  /** Call from a user gesture: create or resume the audio context ("suspended", or Safari's "interrupted"). */
  unlock() {
    if (!this.enabled || this.hidden) return;
    try {
      if (!this.ctx) this.init(new AudioContext({ latencyHint: "interactive" }));
      const ctx = this.ctx as AudioContext;
      if (ctx.state !== "running") ctx.resume().then(() => this.onBlocked(false)).catch(() => {});
      else this.onBlocked(false);
    } catch {
      /* no audio on this device */
    }
  }

  setEnabled(on: boolean, volume: number) {
    this.enabled = on;
    this.volume = volume;
    if (!on) this.jobs = [];
    const ctx = this.ctx;
    if (!ctx) {
      if (on) this.onBlocked(true);
      return;
    }
    const t = ctx.currentTime;
    this.master.gain.cancelScheduledValues(t);
    this.master.gain.setTargetAtTime(on ? volume : 0, t, 0.08);
    this.wake();
  }

  /** The page was hidden or shown: a hidden page makes no sound and spends no battery on it. */
  setHidden(hidden: boolean) {
    this.hidden = hidden;
    this.wake();
  }

  /** Run the audio context only while sound is on and the page is visible (a suspended context costs nothing). */
  private wake() {
    const ctx = this.ctx;
    if (!ctx || !this.live) return;
    const ac = ctx as AudioContext;
    if (this.enabled && !this.hidden) {
      if (ac.state !== "running") ac.resume().then(() => this.onBlocked(false)).catch(() => this.onBlocked(true));
    } else if (ac.state === "running") {
      // let the master fade out first
      window.setTimeout(() => { if (!this.enabled || this.hidden) ac.suspend().catch(() => {}); }, 300);
    }
  }

  /** Build the mixing graph on a context: the browser's, or an offline one to render a scene to a file. */
  init(ctx: BaseAudioContext) {
    this.ctx = ctx;
    this.live = typeof AudioContext !== "undefined" && ctx instanceof AudioContext;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -16;
    comp.knee.value = 10;
    comp.ratio.value = 5;
    comp.attack.value = 0.003;
    comp.release.value = 0.3;
    this.master = ctx.createGain();
    this.master.gain.value = this.enabled ? this.volume : 0;
    comp.connect(this.master).connect(ctx.destination);
    this.bus = ctx.createGain();
    this.bus.connect(comp);
    const conv = ctx.createConvolver();
    conv.buffer = impulse(ctx, 3.4, 2.4);
    this.verb = ctx.createGain();
    const wet = ctx.createGain();
    wet.gain.value = 0.55;
    this.verb.connect(conv).connect(wet).connect(comp);
    this.fight = ctx.createGain();
    this.fight.connect(this.bus);
    this.fightVerb = ctx.createGain();
    this.fightVerb.connect(this.verb);
    try { // a silent, steady input: Chrome stops processing a node once all its inputs have gone quiet, which freezes its
      // fades (the battle sounds would stay turned down after a moment of quiet, until the next one woke the bus)
      const keep = ctx.createConstantSource();
      keep.offset.value = 0;
      keep.connect(this.fight);
      keep.connect(this.fightVerb);
      keep.start();
    } catch {
      /* no ConstantSourceNode: fades resume with the next sound */
    }
    this.white = noiseBuffer(ctx, 2, "white");
    this.brown = noiseBuffer(ctx, 4, "brown");
    this.pink = noiseBuffer(ctx, 3, "pink");
  }

  private now(): number | null {
    const c = this.ctx;
    return c && this.enabled && (!this.live || c.state === "running") ? c.currentTime : null;
  }

  // --- the period sounds: rendering and keeping them ------------------------------------------------------

  /** Get a moment's period sounds ready: rendered in a worker (or, failing that, bit by bit when the page is idle). */
  prepare(t: number) {
    const era = eraOf(t);
    if (!this.ctx || this.prepared.has(era)) return;
    this.prepared.add(era);
    for (const id of samplesFor(t)) this.want(id);
    this.want("fire"); // the flames of the ambience, in every period
  }

  /** Whether every sample these cues use has at least one variant ready. */
  has(cues: Cue[]): boolean {
    return cues.every((q) => LIVE.has(q.s) || this.gaveUp.has(q.s as SampleId) || !!this.bank.get(q.s as SampleId)?.length);
  }

  /** Render a sample's variants now, here (offline rendering of a scene). */
  renderNow(ids: Iterable<SampleId>) {
    for (const id of ids) {
      for (let v = 0; v < RECIPES[id].variants; v++) {
        const key = `${id}:${v}`;
        if (this.asked.has(key)) continue;
        this.asked.add(key);
        this.pending.add(key);
        this.keep({ id, v, ...renderSample(id, v) });
      }
    }
  }

  private want(id: SampleId) {
    for (let v = 0; v < RECIPES[id].variants; v++) {
      const key = `${id}:${v}`;
      if (this.asked.has(key)) continue;
      this.asked.add(key);
      this.pending.add(key);
      const w = this.renderer();
      if (w) {
        w.postMessage({ id, v });
        if (!this.watchdog) this.watchdog = window.setTimeout(() => this.noWorker(w), this.workerReady ? 8000 : 30000);
      } else this.later({ id, v });
    }
  }

  private renderer(): Worker | null {
    if (this.worker !== undefined) return this.worker;
    try {
      const w = new Worker(new URL("./foley.worker.ts", import.meta.url), { type: "module" });
      w.onmessage = (e: MessageEvent<Rendered | { ready: true }>) => {
        window.clearTimeout(this.watchdog);
        this.watchdog = 0;
        this.workerReady = true; // loaded: from now on it answers each sound within seconds, or is given up on
        if (!("ready" in e.data)) this.keep(e.data);
        if (this.pending.size) this.watchdog = window.setTimeout(() => this.noWorker(w), 8000);
      };
      w.onerror = () => this.noWorker(w);
      this.worker = w;
    } catch {
      this.worker = null;
    }
    return this.worker;
  }

  /** The worker failed to load or stopped answering: make what is still owed here, bit by bit. */
  private noWorker(w: Worker) {
    if (this.worker !== w) return;
    window.clearTimeout(this.watchdog);
    this.watchdog = 0;
    this.worker = null;
    w.terminate();
    for (const key of this.pending) {
      const [id, v] = key.split(":");
      this.later({ id: id as SampleId, v: Number(v) });
    }
  }

  private later(job: { id: SampleId; v: number }) {
    if (RECIPES[job.id].heavy) { // a crowd's roar takes a few tenths of a second to make: not on the main thread
      this.pending.delete(`${job.id}:${job.v}`);
      this.gaveUp.add(job.id);
      return;
    }
    this.queue.push(job);
    if (this.pumping) return;
    this.pumping = true;
    const step = () => {
      const j = this.queue.shift();
      if (!j) { this.pumping = false; return; }
      try {
        if (this.pending.has(`${j.id}:${j.v}`)) this.keep({ id: j.id, v: j.v, ...renderSample(j.id, j.v) });
      } catch {
        this.pending.delete(`${j.id}:${j.v}`); // that one stays unmade; the rest go on
      } finally {
        idle(step);
      }
    };
    idle(step);
  }

  private keep(m: Rendered) {
    const ctx = this.ctx, key = `${m.id}:${m.v}`;
    if (!ctx || !this.pending.delete(key)) return;
    const buf = ctx.createBuffer(m.channels.length, m.channels[0].length, m.sr);
    m.channels.forEach((c, k) => buf.copyToChannel(c as Float32Array<ArrayBuffer>, k));
    const list = this.bank.get(m.id) ?? [];
    list.push(buf);
    this.bank.set(m.id, list);
  }

  // --- voices -------------------------------------------------------------------------------------------------

  /** A voice's output: panned, level and reverb set by distance; counted while it sounds. Battle sounds go through the
   * fight bus; solemn ones (the bell, the camera, captions) do not. */
  private voice(p: Place, level: number, seconds: number, solemn = false): AudioNode | null {
    const ctx = this.ctx!;
    if (this.live) {
      if (this.voices >= MAX_VOICES) return null;
      this.voices++;
      window.setTimeout(() => { this.voices--; }, seconds * 1000 + 200);
    }
    const g = ctx.createGain();
    g.gain.value = level * (1 - 0.8 * p.dist) * (ctx.currentTime < this.duckUntil ? 0.25 : 1);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 600 + 9000 * Math.pow(1 - p.dist, 2); // far away is muffled
    const pan = ctx.createStereoPanner();
    pan.pan.value = p.pan;
    lp.connect(g).connect(pan).connect(solemn ? this.bus : this.fight);
    const send = ctx.createGain();
    send.gain.value = 0.18 + 0.6 * p.dist;
    pan.connect(send).connect(solemn ? this.verb : this.fightVerb);
    return lp;
  }

  private noise(buf: AudioBuffer, at: number, dur: number): AudioBufferSourceNode {
    const src = this.ctx!.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    src.start(at, Math.random() * (buf.duration - 0.1));
    src.stop(at + dur + 0.05);
    return src;
  }

  private env(g: AudioParam, at: number, peak: number, attack: number, decay: number) {
    g.setValueAtTime(0.0001, at);
    g.exponentialRampToValueAtTime(peak, at + attack);
    g.exponentialRampToValueAtTime(0.0001, at + attack + decay);
  }

  /** Throttle: no more than n sounds of a kind within a window of w seconds. */
  private allow(kind: string, t: number, n = 6, w = 1): boolean {
    const list = (this.recent.get(kind) ?? []).filter((x) => t - x < w);
    const ok = list.length < n;
    if (ok) list.push(t);
    this.recent.set(kind, list);
    return ok;
  }

  // --- what the director asks for -----------------------------------------------------------------------------

  /** An event, as its period heard it (score.ts). */
  event(fx: number, t: number, m: number, p: Place, chapter = false) {
    const t0 = this.now();
    if (t0 == null) return;
    if (fx === FX_NUKE) { this.nuclear(); return; }
    if (fx === FX_MEMORIAL) { // a bell, never kept from ringing by battles (it has its own pace); a chapter's always rings
      if (m >= 0.3 || chapter) this.bell(m, p, chapter);
      return;
    }
    if (!this.allow("event", t0, 2, 1)) return;
    this.prepare(t);
    this.play(scoreEvent(fx, t, m, Math.random), p);
  }

  /** A moment of quiet, such as a replay's chapter about an atrocity: no fighting heard around, no distant guns, the
   * ambience low (the bell is still heard). */
  setQuiet(quiet: boolean) {
    if (quiet === this.quiet) return;
    this.quiet = quiet;
    if (quiet) this.jobs = this.jobs.filter((j) => j.bell);
    const ctx = this.ctx;
    if (!ctx || !this.live) return;
    const t = ctx.currentTime;
    for (const x of [this.fight.gain, this.fightVerb.gain]) {
      x.cancelScheduledValues(t);
      x.setTargetAtTime(quiet ? 0.08 : 1, t, quiet ? 0.15 : 0.8);
    }
  }

  /** A big battle off screen, heard far away. */
  distant(t: number, m: number, pan: number) {
    const t0 = this.now();
    if (t0 == null || this.quiet || t0 - this.lastBell < 4 || !this.allow("distant", t0, 1, 1.2)) return;
    this.play(scoreDistant(t, m, Math.random), { dist: 0.9, pan }, 0.6);
  }

  /** The fighting around the places burning on screen (called a few times a second): now and then a small piece of
   * the period's battle, more often the heavier the fighting (level 0–1). Silent for a while after a bell. */
  battlefield(level: number, t: number, heavy: boolean, place: () => Place | null) {
    const t0 = this.now();
    if (t0 == null || this.quiet || level < 0.04 || t0 < this.duckUntil || t0 - this.lastBell < 3) return;
    if (Math.random() > level * (heavy ? 1.4 : 0.7) * 0.25) return;
    const p = place();
    if (p) this.play(scoreAmbient(t, Math.random), p, 0);
  }

  /** A battle of a period, close by (the settings menu lets people hear each package). */
  async preview(t: number): Promise<void> {
    this.unlock();
    const ctx = this.ctx;
    if (!ctx) return;
    if (this.live && ctx.state !== "running") await (ctx as AudioContext).resume().catch(() => {});
    const cues = scoreEvent(FX_BATTLE, t, 0.75, Math.random);
    for (const q of cues) if (!LIVE.has(q.s)) this.want(q.s as SampleId);
    this.prepare(t);
    for (let k = 0; k < 40 && !this.has(cues); k++) await new Promise((done) => window.setTimeout(done, 100));
    this.play(cues, { dist: 0.12, pan: 0 }, 0);
  }

  /** Play a score at a place: each cue on its own voice (its panning and distance), its repeats on that voice. Cues
   * are built shortly before they sound, so an event's work is spread over its seconds instead of one frame. */
  play(cues: Cue[], p: Place, delay = p.dist * 0.45) {
    const t0 = this.now();
    if (t0 == null) return;
    for (const c of cues) {
      const place = { dist: clamp(p.dist + (c.far ?? 0), 0, 1), pan: clamp(p.pan + (c.pan ?? 0), -1, 1) };
      const at = t0 + delay + c.at;
      if (!this.live || at - t0 <= LEAD) this.cue(c, at, place);
      else this.jobs.push({ due: at - LEAD, bell: c.s === "bell", run: () => this.cue(c, at, place) });
    }
    if (this.jobs.length && !this.ticker) this.ticker = window.setInterval(() => this.buildDue(), 100);
  }

  /** Build the cues coming due. */
  private buildDue() {
    const ctx = this.ctx;
    if (!ctx || !this.jobs.length) { window.clearInterval(this.ticker); this.ticker = 0; return; }
    const now = ctx.currentTime, due = this.jobs.filter((j) => j.due <= now + 0.05);
    if (!due.length) return;
    this.jobs = this.jobs.filter((j) => j.due > now + 0.05);
    for (const j of due) j.run();
  }

  private cue(c: Cue, at: number, p: Place) {
    if (this.now() == null || (this.quiet && c.s !== "bell")) return;
    if (this.voices > MAX_VOICES * 0.6 && c.g < 0.45) return; // a crowded moment: the quieter parts give way
    if (LIVE.has(c.s)) this.liveCue(c, at, p);
    else this.sample(c, at, p);
  }

  private sample(c: Cue, at: number, p: Place) {
    const ctx = this.ctx!, id = c.s as SampleId, list = this.bank.get(id);
    if (!list?.length) { this.want(id); return; } // not made yet: next time
    const n = c.n ?? 1, rate = c.rate ?? 1, loop = !!c.len && !!RECIPES[id].loop;
    const longest = Math.max(...list.map((b) => b.duration)) / rate;
    const span = c.every ? c.every * n : c.within ?? 0, hold = c.len ?? longest;
    const out = this.voice(p, c.g, at - ctx.currentTime + span + hold);
    if (!out) return;
    let dest: AudioNode = out;
    if (c.len || c.sweep) { // held for a while (an engine, a rotor): fades in and out; passing overhead: across the sky
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, at);
      if (c.sweep) {
        g.gain.exponentialRampToValueAtTime(1, at + hold * 0.45);
        g.gain.exponentialRampToValueAtTime(0.0001, at + hold);
        const pan = ctx.createStereoPanner();
        pan.pan.setValueAtTime(-c.sweep, at);
        pan.pan.linearRampToValueAtTime(c.sweep, at + hold);
        g.connect(pan).connect(out);
      } else {
        g.gain.exponentialRampToValueAtTime(1, at + Math.min(0.4, hold * 0.2));
        g.gain.setValueAtTime(1, at + hold * 0.75);
        g.gain.exponentialRampToValueAtTime(0.0001, at + hold);
        g.connect(out);
      }
      dest = g;
    }
    // many hands (a volley, scattered fire): some nearer than others, in three groups rather than a gain each
    const groups = c.within && n > 1 ? [1, 0.75, 0.5].map((v) => { const g = ctx.createGain(); g.gain.value = v; g.connect(dest); return g; }) : null;
    for (const offset of repeatTimes(c, Math.random)) {
      const when = at + offset;
      const src = ctx.createBufferSource();
      src.buffer = list[Math.floor(Math.random() * list.length)];
      const r = rate * (0.96 + Math.random() * 0.08);
      if (c.sweep) { // the note falls as it passes (Doppler)
        src.playbackRate.setValueAtTime(r * 1.04, when);
        src.playbackRate.linearRampToValueAtTime(r * 0.96, when + hold);
      } else src.playbackRate.value = r;
      src.connect(groups ? groups[Math.floor(Math.random() * 3)] : dest);
      if (loop) src.loop = true;
      src.start(when, loop ? Math.random() * src.buffer.duration : 0);
      if (c.len) src.stop(when + c.len + 0.05);
    }
  }

  private liveCue(c: Cue, at: number, p: Place) {
    const len = c.len ?? 1, g = c.g;
    switch (c.s) {
      case "boom": this.boom(g, p, at); break;
      case "bell": this.bell(g, p); break;
      case "whistle": this.whistle(at, len, p, g, c.rate ?? 1); break;
      case "siren": this.siren(at, len, p, g); break;
      case "stuka": this.stuka(at, len, p, g); break;
      case "planes": this.planes(at, len, p, g, c.rate ?? 1, c.sweep ?? 0.8); break;
      case "jet": this.jet(at, len, p, g, c.sweep ?? 1); break;
      case "drone": this.drone(at, len, p, g); break;
    }
  }

  // --- sounds made live -----------------------------------------------------------------------------------------

  /** A big battle's low end: the ground's thump and the rumble that follows. */
  private boom(m: number, p: Place, at: number) {
    const ctx = this.ctx!, len = 1.4 + 2.2 * m;
    const out = this.voice(p, 0.5 + 0.6 * m, at - ctx.currentTime + len + 0.3);
    if (!out) return;
    const o = ctx.createOscillator();
    o.type = "sine";
    o.frequency.setValueAtTime(72 - 22 * m, at);
    o.frequency.exponentialRampToValueAtTime(26, at + 0.5 + 0.35 * m);
    const og = ctx.createGain();
    this.env(og.gain, at, 1.1, 0.006, 0.7 + 0.9 * m);
    o.connect(og).connect(out);
    o.start(at);
    o.stop(at + 2);
    const n = this.noise(this.brown, at, len);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.Q.value = 0.7;
    lp.frequency.setValueAtTime(2600, at);
    lp.frequency.exponentialRampToValueAtTime(90, at + len);
    const ng = ctx.createGain();
    this.env(ng.gain, at, 0.95, 0.012, len);
    n.connect(lp).connect(ng).connect(out);
  }

  /** A shell or a bomb coming down: a falling whistle, louder as it nears (the burst is its own cue). */
  private whistle(at: number, len: number, p: Place, level: number, rate: number) {
    const ctx = this.ctx!;
    const out = this.voice(p, level, at - ctx.currentTime + len + 0.2);
    if (!out) return;
    const f0 = 1500 * rate, f1 = 520 * rate;
    const o = ctx.createOscillator();
    o.type = "sine";
    o.frequency.setValueAtTime(f0, at);
    o.frequency.exponentialRampToValueAtTime(f1, at + len);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.0001, at);
    og.gain.exponentialRampToValueAtTime(0.4, at + len * 0.9);
    og.gain.linearRampToValueAtTime(0.0001, at + len);
    o.connect(og).connect(out);
    o.start(at);
    o.stop(at + len + 0.05);
    const n = this.noise(this.white, at, len), bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.Q.value = 3;
    bp.frequency.setValueAtTime(f0, at);
    bp.frequency.exponentialRampToValueAtTime(f1, at + len);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.0001, at);
    ng.gain.exponentialRampToValueAtTime(0.5, at + len * 0.9);
    ng.gain.linearRampToValueAtTime(0.0001, at + len);
    n.connect(bp).connect(ng).connect(out);
  }

  /** An air-raid siren over the town: rising and falling. */
  private siren(at: number, len: number, p: Place, level: number) {
    const ctx = this.ctx!;
    if (!this.allow("siren", ctx.currentTime, 1, 15)) return;
    const out = this.voice(p, level, at - ctx.currentTime + len + 0.3);
    if (!out) return;
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 1800;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(0.35, at + 1.2);
    g.gain.setValueAtTime(0.35, at + len - 1.6);
    g.gain.exponentialRampToValueAtTime(0.0001, at + len);
    lp.connect(g).connect(out);
    for (const k of [1, 1.19]) { // two rotors a minor third apart
      const o = ctx.createOscillator();
      o.type = "sawtooth";
      const f = o.frequency;
      f.setValueAtTime(110 * k, at);
      f.exponentialRampToValueAtTime(430 * k, at + len * 0.26);
      f.linearRampToValueAtTime(300 * k, at + len * 0.46);
      f.exponentialRampToValueAtTime(440 * k, at + len * 0.66);
      f.exponentialRampToValueAtTime(120 * k, at + len);
      o.connect(lp);
      o.start(at);
      o.stop(at + len + 0.05);
    }
  }

  /** A dive bomber's siren, screaming louder and higher as it dives. */
  private stuka(at: number, len: number, p: Place, level: number) {
    const ctx = this.ctx!;
    const out = this.voice(p, level, at - ctx.currentTime + len + 0.3);
    if (!out) return;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.Q.value = 1.4;
    bp.frequency.setValueAtTime(700, at);
    bp.frequency.exponentialRampToValueAtTime(1800, at + len);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(0.5, at + len);
    g.gain.linearRampToValueAtTime(0.0001, at + len + 0.15);
    bp.connect(g).connect(out);
    const o = ctx.createOscillator();
    o.type = "sawtooth";
    o.frequency.setValueAtTime(300, at);
    o.frequency.exponentialRampToValueAtTime(950, at + len);
    o.connect(bp);
    const e = ctx.createOscillator(); // the engine at full throttle
    e.type = "sawtooth";
    e.frequency.setValueAtTime(95, at);
    e.frequency.exponentialRampToValueAtTime(150, at + len);
    const eg = ctx.createGain();
    eg.gain.value = 0.6;
    e.connect(eg).connect(bp);
    for (const x of [o, e]) { x.start(at); x.stop(at + len + 0.2); }
  }

  /** Propeller aircraft passing over: engines beating against each other, the note falling as they pass. */
  private planes(at: number, len: number, p: Place, level: number, rate: number, sweep: number) {
    const ctx = this.ctx!;
    if (!this.allow("planes", ctx.currentTime, 1, 3)) return;
    const out = this.voice({ dist: Math.min(0.6, p.dist), pan: 0 }, level, at - ctx.currentTime + len + 0.3);
    if (!out) return;
    const pan = ctx.createStereoPanner();
    pan.pan.setValueAtTime(-sweep, at);
    pan.pan.linearRampToValueAtTime(sweep, at + len);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 520 * rate;
    const am = ctx.createGain(); // the propellers' beat
    am.gain.value = 0.7;
    const lfo = ctx.createOscillator(), depth = ctx.createGain();
    lfo.frequency.value = 18 * rate;
    depth.gain.value = 0.3;
    lfo.connect(depth).connect(am.gain);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(0.5, at + len * 0.4);
    g.gain.setValueAtTime(0.5, at + len * 0.55);
    g.gain.exponentialRampToValueAtTime(0.0001, at + len);
    lp.connect(am).connect(g).connect(pan).connect(out);
    for (const f of [86, 89.5, 172.5]) {
      const o = ctx.createOscillator();
      o.type = "sawtooth";
      o.frequency.setValueAtTime(f * rate * 1.04, at);
      o.frequency.linearRampToValueAtTime(f * rate * 0.94, at + len); // passing overhead
      o.connect(lp);
      o.start(at);
      o.stop(at + len + 0.1);
    }
    lfo.start(at);
    lfo.stop(at + len + 0.1);
  }

  /** A jet passing low: a tearing roar that sweeps across, its whine falling. */
  private jet(at: number, len: number, p: Place, level: number, sweep: number) {
    const ctx = this.ctx!;
    if (!this.allow("jet", ctx.currentTime, 1, 3)) return;
    const out = this.voice({ dist: Math.min(0.5, p.dist), pan: 0 }, level, at - ctx.currentTime + len + 0.3);
    if (!out) return;
    const pan = ctx.createStereoPanner();
    pan.pan.setValueAtTime(-sweep, at);
    pan.pan.linearRampToValueAtTime(sweep, at + len);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(0.9, at + len * 0.5);
    g.gain.exponentialRampToValueAtTime(0.0001, at + len);
    g.connect(pan).connect(out);
    const roar = this.noise(this.white, at, len), bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.Q.value = 0.7;
    bp.frequency.setValueAtTime(2800, at);
    bp.frequency.exponentialRampToValueAtTime(700, at + len);
    roar.connect(bp).connect(g);
    const rumble = this.noise(this.brown, at, len), lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 300;
    rumble.connect(lp).connect(g);
    const w = ctx.createOscillator(), wg = ctx.createGain();
    w.frequency.setValueAtTime(4200, at);
    w.frequency.exponentialRampToValueAtTime(2400, at + len);
    wg.gain.value = 0.04;
    w.connect(wg).connect(g);
    w.start(at);
    w.stop(at + len + 0.05);
  }

  /** A small drone coming in: a buzzing that rises as it nears (its strike is its own cue). */
  private drone(at: number, len: number, p: Place, level: number) {
    const ctx = this.ctx!;
    const out = this.voice(p, level, at - ctx.currentTime + len + 0.2);
    if (!out) return;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.Q.value = 0.8;
    bp.frequency.value = 1100;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(0.45, at + len);
    g.gain.linearRampToValueAtTime(0.0001, at + len + 0.05);
    bp.connect(g).connect(out);
    const lfo = ctx.createOscillator(), depth = ctx.createGain(); // motors hunting for speed
    lfo.frequency.value = 3.3;
    depth.gain.value = 12;
    lfo.connect(depth);
    for (const [f, a] of [[185, 1], [370, 0.3]]) {
      const o = ctx.createOscillator(), og = ctx.createGain();
      o.type = "sawtooth";
      o.frequency.setValueAtTime(f, at);
      o.frequency.linearRampToValueAtTime(f * 1.08, at + len);
      depth.connect(o.detune);
      og.gain.value = a;
      o.connect(og).connect(bp);
      o.start(at);
      o.stop(at + len + 0.1);
    }
    lfo.start(at);
    lfo.stop(at + len + 0.1);
  }

  /** A distant bell for massacres and deportations (at most one every eight seconds). */
  bell(m: number, p: Place, chapter = false) {
    const t0 = this.now();
    if (t0 == null || t0 - this.lastBell < (chapter ? 2 : 8)) return; // a chapter's rings unless one is ringing now
    this.lastBell = t0;
    if (this.live) { // the battle sounds step back while it rings
      const g = this.fight.gain, v = this.fightVerb.gain, low = this.quiet ? 0.08 : 0.3;
      for (const x of [g, v]) {
        x.cancelScheduledValues(t0);
        x.setTargetAtTime(low, t0, 0.15);
        if (!this.quiet) x.setTargetAtTime(1, t0 + 3, 0.8);
      }
    }
    const out = this.voice({ dist: Math.min(0.7, p.dist + 0.3), pan: p.pan }, 0.16 + 0.12 * m, 7, true);
    if (!out) return;
    const ctx = this.ctx!, f0 = 98;
    [[0.5, 0.5, 6], [1, 1, 5], [1.183, 0.5, 3.5], [1.506, 0.45, 3], [2, 0.35, 2.6], [2.514, 0.25, 2], [3.011, 0.18, 1.5]].forEach(([r, a, d]) => {
      const o = ctx.createOscillator();
      o.type = "sine";
      o.frequency.value = f0 * r;
      const g = ctx.createGain();
      this.env(g.gain, t0, a * 0.5, 0.004, d);
      o.connect(g).connect(out);
      o.start(t0);
      o.stop(t0 + d + 0.1);
    });
  }

  /** The atomic bombings: silence after the flash, then a long, enormous rumble. */
  nuclear() {
    const t0 = this.now();
    if (t0 == null) return;
    this.duckUntil = t0 + 9;
    const ctx = this.ctx!, at = t0 + 1.1, len = 9;
    const out = this.voice({ dist: 0.2, pan: 0 }, 1.1, len + 1.5);
    if (!out) return;
    const o = ctx.createOscillator();
    o.type = "sine";
    o.frequency.setValueAtTime(38, at);
    o.frequency.exponentialRampToValueAtTime(20, at + len);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.0001, at);
    og.gain.exponentialRampToValueAtTime(1.2, at + 0.35);
    og.gain.exponentialRampToValueAtTime(0.0001, at + len);
    o.connect(og).connect(out);
    o.start(at);
    o.stop(at + len + 0.1);
    const n = this.noise(this.brown, at, len);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(700, at);
    lp.frequency.exponentialRampToValueAtTime(55, at + len);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.0001, at);
    ng.gain.exponentialRampToValueAtTime(1.3, at + 0.6);
    ng.gain.exponentialRampToValueAtTime(0.0001, at + len);
    n.connect(lp).connect(ng).connect(out);
    if (this.bed) {
      for (const g of [this.bed.g, this.bed.wind, this.bed.fire]) {
        if (!g) continue;
        g.gain.cancelScheduledValues(t0);
        g.gain.setTargetAtTime(0.0001, t0, 0.05);
      }
    }
  }

  /** A chapter opens: a low cinematic hit. */
  stinger(era: number) {
    const t0 = this.now();
    if (t0 == null || t0 - this.lastStinger < 2.5) return;
    this.lastStinger = t0;
    const out = this.voice({ dist: 0.1, pan: 0 }, 0.42, 3.4, true);
    if (!out) return;
    const ctx = this.ctx!;
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.Q.value = 3;
    lp.frequency.setValueAtTime(140, t0);
    lp.frequency.exponentialRampToValueAtTime(era === ERA_BLADE ? 500 : 950, t0 + 0.18);
    lp.frequency.exponentialRampToValueAtTime(120, t0 + 2.6);
    const g = ctx.createGain();
    this.env(g.gain, t0, 0.5, 0.04, 2.6);
    lp.connect(g).connect(out);
    for (const [f, det] of [[55, -6], [55, 7], [82.4, 0], [110, 4]]) {
      const o = ctx.createOscillator();
      o.type = "sawtooth";
      o.frequency.value = f;
      o.detune.value = det;
      o.connect(lp);
      o.start(t0);
      o.stop(t0 + 2.8);
    }
    const s = ctx.createOscillator();
    s.type = "sine";
    s.frequency.setValueAtTime(64, t0);
    s.frequency.exponentialRampToValueAtTime(30, t0 + 1);
    const sg = ctx.createGain();
    this.env(sg.gain, t0, 0.9, 0.005, 1.1);
    s.connect(sg).connect(out);
    s.start(t0);
    s.stop(t0 + 1.3);
  }

  /** The camera sweeps to a new place. */
  whoosh(seconds: number) {
    const t0 = this.now();
    if (t0 == null) return;
    const out = this.voice({ dist: 0.3, pan: 0 }, 0.12, seconds + 0.3, true);
    if (!out) return;
    const ctx = this.ctx!;
    const src = this.noise(this.pink, t0, seconds);
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.Q.value = 1.2;
    bp.frequency.setValueAtTime(260, t0);
    bp.frequency.exponentialRampToValueAtTime(1300, t0 + seconds * 0.45);
    bp.frequency.exponentialRampToValueAtTime(240, t0 + seconds);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.6, t0 + seconds * 0.45);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + seconds);
    src.connect(bp).connect(g).connect(out);
  }

  /** Typewriter keys for captions. */
  tick() {
    const t0 = this.now();
    if (t0 == null) return;
    const out = this.voice({ dist: 0, pan: (Math.random() - 0.5) * 0.3 }, 0.05, 0.1, true);
    if (!out) return;
    const ctx = this.ctx!;
    const src = this.noise(this.white, t0, 0.03);
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 2500;
    const g = ctx.createGain();
    this.env(g.gain, t0, 0.6, 0.001, 0.025);
    src.connect(hp).connect(g).connect(out);
  }

  /** The war's ambience, rising with the fighting on screen (level 0–1): wind over old battlefields, the rumble of
   * guns from the age of gunpowder on, flames where places burn (fire 0–1); a low drone while a replay runs. */
  ambience(level: number, drone: boolean, t: number, fire: number) {
    const ctx = this.ctx;
    if (!ctx || (this.live && ctx.state !== "running")) return;
    this.prepare(t);
    if (!this.bed) {
      const src = ctx.createBufferSource();
      src.buffer = this.brown;
      src.loop = true;
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 300;
      const g = ctx.createGain();
      g.gain.value = 0.0001;
      src.connect(lp).connect(g).connect(this.bus);
      src.start();
      const ws = ctx.createBufferSource(); // wind: noise in a band that wanders slowly
      ws.buffer = this.pink;
      ws.loop = true;
      const wbp = ctx.createBiquadFilter();
      wbp.type = "bandpass";
      wbp.frequency.value = 520;
      wbp.Q.value = 0.6;
      const wlfo = ctx.createOscillator(), wdepth = ctx.createGain();
      wlfo.frequency.value = 0.05;
      wdepth.gain.value = 220;
      wlfo.connect(wdepth).connect(wbp.frequency);
      wlfo.start();
      const wind = ctx.createGain();
      wind.gain.value = 0.0001;
      ws.connect(wbp).connect(wind).connect(this.bus);
      ws.start();
      const droneLp = ctx.createBiquadFilter();
      droneLp.type = "lowpass";
      droneLp.frequency.value = 260;
      droneLp.Q.value = 4;
      const dg = ctx.createGain();
      dg.gain.value = 0.0001;
      for (const [f, det] of [[55, -5], [55, 6], [82.4, 0], [65.4, 3]]) {
        const o = ctx.createOscillator();
        o.type = "triangle";
        o.frequency.value = f;
        o.detune.value = det;
        o.connect(droneLp);
        o.start();
      }
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.07;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 90;
      lfo.connect(lfoGain).connect(droneLp.frequency);
      lfo.start();
      droneLp.connect(dg).connect(this.bus);
      this.bed = { g, lp, wind, drone: dg, fire: null };
    }
    const flames = this.bank.get("fire");
    if (!this.bed.fire && flames?.length) {
      const src = ctx.createBufferSource(), g = ctx.createGain();
      src.buffer = flames[0];
      src.loop = true;
      g.gain.value = 0.0001;
      src.connect(g).connect(this.bus);
      src.start();
      this.bed.fire = g;
    }
    const now = ctx.currentTime;
    if (now < this.duckUntil) return;
    if (this.quiet) { level = 0; fire = 0; }
    const era = eraOf(t);
    const rumble = era === ERA_BLADE ? 0.35 : era === ERA_POWDER ? 0.7 : 1, wind = era === ERA_BLADE ? 1 : era === ERA_POWDER ? 0.6 : 0.35;
    this.bed.g.gain.setTargetAtTime((0.02 + 0.16 * level) * rumble, now, 0.6);
    this.bed.lp.frequency.setTargetAtTime(220 + 380 * level, now, 0.8);
    this.bed.wind.gain.setTargetAtTime((0.012 + 0.05 * level) * wind, now, 0.8);
    this.bed.fire?.gain.setTargetAtTime(Math.max(0.0001, 0.22 * fire), now, 0.6);
    this.bed.drone.gain.setTargetAtTime(drone ? 0.035 + 0.03 * level : 0.0001, now, 1.2);
  }

  /** Stop the ambience (effects off, page hidden), and what was still to come. */
  hush() {
    this.jobs = [];
    const ctx = this.ctx;
    if (!ctx || !this.bed) return;
    const t = ctx.currentTime;
    for (const g of [this.bed.g, this.bed.wind, this.bed.drone, this.bed.fire]) g?.gain.setTargetAtTime(0.0001, t, 0.2);
  }
}

export const audio = new Engine();
